import type {
  ImageAssetType,
  ImageModelCaches
} from '@/components/ui/carousel/carousel.types';
import type { Asset } from '@/types/asset.types';
import type { VirtualStagingRender } from './stagingTypes';

/** Exact caption prefix from the DV-2252 AC. */
export const STAGED_CAPTION_PREFIX = 'VS - ';

/**
 * Builds the working-set item for a COMMITTED staged render. Identity comes
 * from the persisted asset (real `assetUUID`/`photoUUID`), so the item is
 * backend-addressable like any other photo and survives refresh. The `staged`
 * flag + room/style are kept for the UI (badge, Virtually Staged tab); they are
 * session-local until the backend hydrates them on reload (follow-up).
 */
export function toStagedImageAsset(
  committed: Asset,
  original: ImageAssetType,
  render: VirtualStagingRender
): ImageAssetType {
  return {
    photoUUID: committed.photoUUID,
    assetUUID: committed.assetUUID,
    orderUUID: committed.orderUUID || original.orderUUID,
    propertyUUID: committed.propertyUUID || original.propertyUUID,
    ...(original.photosetUUID ? { photosetUUID: original.photosetUUID } : {}),
    caption:
      committed.caption ?? `${STAGED_CAPTION_PREFIX}${original.caption ?? ''}`,
    caches: {
      ...((committed.caches ?? render.resultCaches ?? {}) as ImageModelCaches)
    },
    sortOrder: committed.sortOrder,
    // DV-3384: the commit mints an imageID and stamps createdAt on the staged
    // asset (VirtualStagingServiceImpl), so "Show Image Details" has them at
    // once, not after a reload.
    imageID: committed.imageID ?? null,
    createdAt: committed.createdAt ?? undefined,
    isChecked: false,
    staged: true,
    // DV-2826: the render's assetUUID IS the original asset staging ran on —
    // an equivalent fallback for the rare case the commit response omits the
    // field, so "Regenerate with AI" is available immediately, pre-reload.
    stagedFromAssetUUID: committed.stagedFromAssetUUID ?? render.assetUUID,
    stagedRoomType: render.roomType,
    stagedStyle: render.style,
    stagedFromPhotoUUID: original.photoUUID
  };
}
